import { FeatureFlagsEnum } from '@omnea-app/packages/universal/feature-flags/featureFlags'
import { recordAuthorization } from './accessRules'
import { all, always, any, ldFlag, omneanOnly, orgFlag } from './availability'
import { creates, hasEntityAuthorization, managesConfigArea, managesPermissions } from './fgaRules'
import {
  adminsOnly,
  adminsOrAssigneeRoles,
  everyRole,
  everyRoleExceptSuppliers,
  nonViewerAdmins,
  superAdmin,
  superAdminOrP2pAdmin,
  type RoleAuthorizationRule,
} from './roleAuthorization'
import type { AuthorizationRule, RecordAccessRule, RecordVerb } from './accessRules'
import type { AvailabilityRule } from './availability'
import type { ConfigArea, PermissionSetEntity } from '@omnea-app/authz'
import type { RouteKey } from '@omnea-app/fe-routing/routes/routeKeys'

/**
 * Which query parameter distinguishes routes sharing one pattern, and the value that selects this
 * route.
 */
export type RouteSegment = { queryParam: string; value: string }

type RouteAddress = {
  /**
   * The Next.js route pattern, as `router.route` reports it. Hand-maintained strings the compiler
   * cannot check against the pages directory: a pattern matching no real page makes its route
   * unresolvable, and so ungated.
   */
  pattern: string
  segment?: RouteSegment
}

/**
 * What reaching a route requires, shared by the routes that require the same thing. No `record` arm:
 * `useRouteAccess` answers every route at once with no record in scope, so one declared here would
 * report `pending` forever. A route's record half is its `record` field, which the gate binds an id to.
 */
type RouteAuthorizationRule = Exclude<AuthorizationRule, { kind: 'record' }>

type RouteAccessRule = {
  /** Required where `AccessRule` leaves it optional: an omission would make a gated module unconditional. */
  availability: AvailabilityRule
  authorization: RouteAuthorizationRule
  deniedRedirectTo?: DeniedRedirectKey
}

/**
 * A denial's destination has to satisfy two things at once: it must admit every caller, or a denial
 * bounces between two routes forever, and it must be reachable without caller-supplied values, or
 * there is no URL to send them to.
 */
type DeniedRedirectKey = 'dashboard' | 'settingsNotifications'

/** A route that shows one record carries its id as `[id]`, which the route gate checks before the page. */
type RecordRoutePattern = `${string}/[id]` | `${string}/[id]/${string}`

/** How a route is addressed as a URL, where its pattern does not already say. */
type RouteNavigation = { params?: undefined; href?: string } | { params: readonly [string, ...string[]]; href?: undefined }

export type RouteDefinition =
  | (RouteAddress & RouteAccessRule & RouteNavigation & { record?: undefined })
  | (RouteAddress &
      RouteAccessRule & {
        /** The page is only as reachable as its contents, so the verb is not always `canView`. */
        record: RecordAccessRule
        pattern: RecordRoutePattern
        params: readonly [string, ...string[]]
        href?: undefined
      })

const SETTINGS_FALLBACK_ROUTE = 'settingsNotifications'

export const DEFAULT_DENIED_REDIRECT = 'dashboard'

/**
 * Most settings pages share one page file and are told apart by their mode segment. The exceptions are
 * the ones with a page file of their own: Next routes a concrete path ahead of the optional catch-all,
 * so a mode segment for those would match nothing and leave the route unresolvable — hence ungated.
 */
const SETTINGS_PATTERN = '/settings/[[...mode]]'

const modeSegment = (value: string): RouteSegment => ({ queryParam: 'mode', value })

/**
 * What a settings route requires: the configuration area it belongs to, answered by the service where
 * it can and by the caller's role otherwise.
 *
 * TODO(Permissions): the service arm deliberately omits the role dimension, so a configuration-area
 * grant alone admits a non-admin. Their writes will still fail — the backend derives a caller's
 * permission role from session claims alone, so no grant lifts them to `admin`, and most settings
 * endpoints exclude the `user` role. Those endpoints MUST authorize on the service before enforcement is
 * rolled out beyond the pilot organisations.
 */
const configGated = (area: ConfigArea, role: RoleAuthorizationRule = superAdmin): RouteAuthorizationRule => ({
  kind: 'fgaOrRole',
  fga: managesConfigArea(area),
  role,
})

/**
 * The rollout is folded into the answer rather than named here: `canManagePermissions` reads false until
 * RELEASE_FGA_CONFIG names the caller, and the enforcement flag plays no part, so permissions can be
 * configured before the organisation enforces them. A denial redirects rather than reporting the page
 * non-existent.
 */
const PERMISSIONS_ROUTE = {
  availability: always(),
  authorization: { kind: 'fga', fga: managesPermissions },
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteAccessRule

/**
 * This admits view-only admins, who must stay read-only once inside: every dashboard write endpoint
 * excludes `viewOnlyAdmin`, and the pages answer `canEdit` from `isNonViewerAdmin` as well as the
 * caller's dashboard role. Both are load-bearing — a write affordance gated on the role alone offers
 * a viewer an action the service refuses.
 */
const REPORTING_ROUTE = {
  availability: always(),
  authorization: { kind: 'role', role: adminsOnly },
  deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
} as const satisfies RouteAccessRule

/** Conversational intake ships to Omneans ahead of the organisations the chat assistant is sold to. */
const AI_ROUTE = {
  availability: any([
    orgFlag(FeatureFlagsEnum.AI_CHAT_ASSISTANT),
    all([orgFlag(FeatureFlagsEnum.OMNEANS_ONLY_CONV_INTAKE), omneanOnly()]),
  ]),
  authorization: { kind: 'open' },
  deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
} as const satisfies RouteAccessRule

/**
 * Each of these is shared by a landing route and the detail pages beneath it, which must answer alike:
 * a nested page left on its own rule stays reachable under terms its parent has stopped allowing.
 */
const AI_BULK_EXTRACTION_ROUTE = {
  availability: ldFlag('RELEASE_AI_BULK_EXTRACTION'),
  authorization: configGated('organisation_settings'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteAccessRule

const DOCUMENT_STORAGE_ROUTE = {
  availability: orgFlag(FeatureFlagsEnum.ENABLE_DOCUMENT_EXTRACTION),
  authorization: configGated('document_review'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteAccessRule

const ENVIRONMENT_SYNC_ROUTE = {
  availability: ldFlag('RELEASE_CONFIG_SYNC'),
  authorization: { kind: 'fgaOrRole', fga: managesConfigArea('workflows_and_forms'), role: superAdmin },
  deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
} as const satisfies RouteAccessRule

const INTEGRATIONS_ROUTE = {
  availability: always(),
  authorization: configGated('it'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteAccessRule

const PUBLIC_API_ROUTE = {
  availability: all([orgFlag(FeatureFlagsEnum.PUBLIC_API_UI), orgFlag(FeatureFlagsEnum.PUBLIC_API)]),
  authorization: configGated('it'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteAccessRule

const INTEGRATION_BUILDER_ROUTE = {
  availability: ldFlag('RELEASE_INTEGRATION_BUILDER_V2'),
  authorization: configGated('it'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteAccessRule

/** Shared with the `/workflows` routes, which mount the same pages outside the settings tree. */
const WORKFLOW_EDITOR_ROUTE = {
  availability: always(),
  authorization: configGated('workflows_and_forms', superAdminOrP2pAdmin),
  deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
} as const satisfies RouteAccessRule

/**
 * Starting a request, drafts included: a draft only resumes into creating one. Shared with the
 * new-request and Drafts links, which must agree with the route they lead
 * to. Before enforcement every app role could start one, so the role arm keeps that.
 */
export const CREATE_REQUEST_ROUTE = {
  availability: always(),
  authorization: { kind: 'fgaOrRole', fga: creates('request'), role: everyRoleExceptSuppliers },
  deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
} as const satisfies RouteAccessRule

// Role arms keep pre-FGA behaviour: the suppliers list was super-admin only, the form picker open to every role.
export const CREATE_SUPPLIER_ROUTE = {
  availability: always(),
  authorization: { kind: 'fgaOrRole', fga: creates('supplier'), role: superAdmin },
} as const satisfies RouteAccessRule

export const CREATE_SUPPLIER_IN_FORM_ROUTE = {
  availability: always(),
  authorization: { kind: 'fgaOrRole', fga: creates('supplier'), role: everyRole },
} as const satisfies RouteAccessRule

/** Keyed by entity so a new permission-set family cannot ship with its list route ungated. */
const ENTITY_LIST_ROUTES = {
  request: {
    availability: always(),
    authorization: { kind: 'fgaOrRole', fga: hasEntityAuthorization('request'), role: everyRoleExceptSuppliers },
  },
  supplier: {
    availability: always(),
    authorization: { kind: 'fgaOrRole', fga: hasEntityAuthorization('supplier'), role: adminsOrAssigneeRoles },
  },
  agreement: {
    availability: orgFlag(FeatureFlagsEnum.CREATE_AGREEMENT),
    authorization: { kind: 'fgaOrRole', fga: hasEntityAuthorization('agreement'), role: everyRole },
  },
  bill: {
    availability: orgFlag(FeatureFlagsEnum.INVOICE_PROCESSING),
    authorization: { kind: 'fgaOrRole', fga: hasEntityAuthorization('bill'), role: adminsOnly },
  },
} as const satisfies Readonly<Record<PermissionSetEntity, RouteAccessRule>>

/**
 * A detail route answers as its list does, then the route gate holds the page until the record's own
 * check allows it. Both halves take their role arm from the one rule, so the record cannot be reached
 * by a caller the list would have turned away — the permissions service answers `role` for a record it
 * does not hold, which an organisation already enforcing still has for anything unmigrated, so that arm
 * has to reproduce what gated the record before the service existed.
 */
const detailRouteFor = <
  Entity extends PermissionSetEntity,
  Pattern extends RecordRoutePattern,
  Params extends readonly [string, ...string[]],
>(
  entity: Entity,
  verb: RecordVerb<Entity>,
  pattern: Pattern,
  params: Params
) => {
  const listRoute = ENTITY_LIST_ROUTES[entity]

  return {
    ...listRoute,
    record: { authorization: recordAuthorization(entity, verb, listRoute.authorization.role) },
    pattern,
    params,
  } as const
}

export const ROUTE_REGISTRY = {
  dashboard: { pattern: '/', availability: always(), authorization: { kind: 'open' } },
  tasks: { pattern: '/tasks/[[...mode]]', availability: always(), authorization: { kind: 'open' } },
  startRequest: { ...CREATE_REQUEST_ROUTE, pattern: '/start' },
  startDrafts: { ...CREATE_REQUEST_ROUTE, pattern: '/start/drafts' },
  ai: { ...AI_ROUTE, pattern: '/ai' },
  aiChat: { ...AI_ROUTE, pattern: '/ai/[mode]', params: ['mode'] },
  requestsList: { ...ENTITY_LIST_ROUTES.request, pattern: '/requests/[[...mode]]' },
  suppliersList: { ...ENTITY_LIST_ROUTES.supplier, pattern: '/suppliers/[[...mode]]' },
  supplierDetailIndex: detailRouteFor('supplier', 'canView', '/suppliers/detail/[id]', ['id']),
  supplierDetail: detailRouteFor('supplier', 'canView', '/suppliers/detail/[id]/[tab]', ['id', 'tab']),
  // The page is the submission, not the supplier, and the two are granted separately.
  supplierDetailSubmission: detailRouteFor(
    'supplier',
    'canViewRelatedSubmissions',
    '/suppliers/detail/[id]/submissions/[submissionId]',
    ['id', 'submissionId']
  ),
  agreementsList: { ...ENTITY_LIST_ROUTES.agreement, pattern: '/agreements/[[...mode]]' },
  agreementDetailIndex: detailRouteFor('agreement', 'canView', '/agreements/detail/[id]', ['id']),
  agreementDetail: detailRouteFor('agreement', 'canView', '/agreements/detail/[id]/[tab]', ['id', 'tab']),
  agreementDetailSubmission: detailRouteFor('agreement', 'canView', '/agreements/detail/[id]/[tab]/[submissionId]', [
    'id',
    'tab',
    'submissionId',
  ]),
  agreementAuditLog: detailRouteFor('agreement', 'canView', '/agreements/detail/[id]/audit-log', ['id']),
  assessmentsList: {
    pattern: '/assessments/[[...mode]]',
    availability: orgFlag(FeatureFlagsEnum.ENABLE_ASSESSMENTS),
    authorization: { kind: 'role', role: adminsOrAssigneeRoles },
  },
  renewalsList: {
    pattern: '/renewals/[[...mode]]',
    availability: always(),
    authorization: { kind: 'fgaOrRole', fga: hasEntityAuthorization('agreement'), role: adminsOrAssigneeRoles },
  },
  documentsList: {
    pattern: '/documents/[[...mode]]',
    availability: always(),
    authorization: { kind: 'role', role: adminsOrAssigneeRoles },
  },
  purchaseOrdersList: {
    pattern: '/purchase-orders/[[...tab]]',
    availability: ldFlag('RELEASE_PURCHASE_ORDER_UPDATES'),
    authorization: { kind: 'role', role: adminsOnly },
  },
  scheduledRequestsList: {
    pattern: '/scheduled-requests/[[...mode]]',
    availability: ldFlag('RELEASE_SCHEDULED_REQUESTS'),
    authorization: { kind: 'role', role: nonViewerAdmins },
  },
  budgetsList: {
    pattern: '/budgets/[[...mode]]',
    availability: orgFlag(FeatureFlagsEnum.BUDGETS),
    authorization: { kind: 'role', role: adminsOnly },
  },
  billsList: { ...ENTITY_LIST_ROUTES.bill, pattern: '/bills/[[...tab]]' },
  productCatalogues: {
    pattern: '/products-and-services',
    availability: any([
      all([orgFlag(FeatureFlagsEnum.PUNCHOUT_CATALOGUES), ldFlag('SHOW_PUNCHOUT_CATALOGUES')]),
      orgFlag(FeatureFlagsEnum.HOSTED_CATALOGUES),
    ]),
    authorization: { kind: 'open' },
  },
  savingsDashboard: {
    pattern: '/sourcing/[[...mode]]',
    availability: ldFlag('RELEASE_S_AND_P_SAVINGS_DASHBOARD'),
    authorization: { kind: 'open' },
  },
  risksList: {
    pattern: '/risks/[[...mode]]',
    availability: orgFlag(FeatureFlagsEnum.TPRM),
    authorization: { kind: 'open' },
  },
  riskAlertsList: {
    pattern: '/risk-alerts/[[...mode]]',
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), orgFlag(FeatureFlagsEnum.TPRM_CONTINUOUS_MONITORING)]),
    authorization: { kind: 'open' },
  },
  subprocessorsList: {
    pattern: '/subprocessors/[[...mode]]',
    availability: orgFlag(FeatureFlagsEnum.SUPPLIER_SUBPROCESSORS),
    authorization: { kind: 'role', role: adminsOnly },
  },
  // Insights opens on Custom Dashboards; linking to the bare `/insights` costs a client redirect.
  insights: { ...REPORTING_ROUTE, pattern: '/insights/[[...path]]', href: '/insights/custom-dashboards' },
  reporting: { ...REPORTING_ROUTE, pattern: '/reporting/[[...path]]' },
  reportingDataDestinations: {
    pattern: '/reporting/data-destinations',
    availability: ldFlag('RELEASE_REPORTING_CLOUD_DELIVERY_UI_V2'),
    authorization: { kind: 'role', role: superAdmin },
    deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
  },
  workflowsList: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/workflows' },
  workflowAuditLog: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/workflows/edit/[id]/audit-log', params: ['id'] },
  workflowDraftEdit: {
    ...WORKFLOW_EDITOR_ROUTE,
    pattern: '/workflows/edit/[id]/drafts/[draftId]',
    params: ['id', 'draftId'],
  },
  workflowEdit: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/workflows/edit/[id]', params: ['id'] },
  settingsPermissions: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions' },
  settingsPermissionsConfigAccess: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions/config-access' },
  settingsPermissionsRoles: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions/roles' },
  settingsPermissionsPermissionSets: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions/permission-sets' },
  settingsNotifications: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('notifications'),
    availability: always(),
    authorization: { kind: 'open' },
  },
  // Both follow the side menu rather than the page: reaching a settings route requires the config area
  // its menu section requires.
  settingsAgentLibrary: {
    pattern: '/settings/agent-library',
    availability: ldFlag('RELEASE_AGENT_MARKETPLACE_V1'),
    authorization: { kind: 'fgaOrRole', fga: managesConfigArea('workflows_and_forms'), role: superAdmin },
    deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
  },
  settingsEnvironmentSync: { ...ENVIRONMENT_SYNC_ROUTE, pattern: '/settings/environment-sync' },
  settingsEnvironmentSyncJob: {
    ...ENVIRONMENT_SYNC_ROUTE,
    pattern: '/settings/environment-sync/[workflowId]/[jobId]',
    params: ['workflowId', 'jobId'],
  },
  settingsAdmin: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('admin'),
    availability: always(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigration: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationConfigureMappings: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-configure-mappings'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationMigrateEngagements: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-migrate-engagements'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationMigrateInFlight: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-migrate-in-flight'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationMigrateRenewals: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-migrate-renewals'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationVerifyInFlight: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-verify-in-flight'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAi: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('ai'),
    availability: always(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAuctions: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('auctions'),
    availability: orgFlag(FeatureFlagsEnum.AUCTIONS),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAiBulkExtraction: {
    ...AI_BULK_EXTRACTION_ROUTE,
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('ai-bulk-extraction'),
  },
  settingsAiBulkExtractionJob: {
    ...AI_BULK_EXTRACTION_ROUTE,
    pattern: '/settings/ai-bulk-extraction/[jobId]',
    params: ['jobId'],
  },
  settingsAiBulkExtractionJobReview: {
    ...AI_BULK_EXTRACTION_ROUTE,
    pattern: '/settings/ai-bulk-extraction/[jobId]/review',
    params: ['jobId'],
  },
  settingsApi: { ...PUBLIC_API_ROUTE, pattern: SETTINGS_PATTERN, segment: modeSegment('api') },
  settingsApiOAuthApp: { ...PUBLIC_API_ROUTE, pattern: '/settings/api/oauth/[integrationId]', params: ['integrationId'] },
  settingsApiOAuthEntities: {
    ...PUBLIC_API_ROUTE,
    pattern: '/settings/api/oauth/[integrationId]/entities',
    params: ['integrationId'],
  },
  settingsApiOAuthEntityFields: {
    ...PUBLIC_API_ROUTE,
    pattern: '/settings/api/oauth/[integrationId]/fields/[entityType]',
    params: ['integrationId', 'entityType'],
  },
  settingsAuditLog: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('audit-log'),
    availability: always(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  // Reached only from a card on the Import page, so it answers to the area that governs Import.
  settingsBulkDocumentsUpload: {
    pattern: '/settings/bulk-documents-upload',
    availability: always(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsExternalArchive: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('external-archive'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAutoFlaggedCategories: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('auto-flagged-categories'),
    availability: orgFlag(FeatureFlagsEnum.TPRM),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsCatalogues: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('catalogues'),
    availability: any([
      orgFlag(FeatureFlagsEnum.PRODUCTS_AND_SERVICES_CATALOGUE),
      orgFlag(FeatureFlagsEnum.PUNCHOUT_CATALOGUES),
      orgFlag(FeatureFlagsEnum.HOSTED_CATALOGUES),
    ]),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsDynamicAssignees: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('dynamic-assignees'),
    availability: always(),
    authorization: configGated('dynamic_assignees'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsCoreData: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('core-data'),
    availability: always(),
    authorization: configGated('core_data'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsDefaultNotifications: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('default-notifications'),
    availability: always(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsDocumentStorage: { ...DOCUMENT_STORAGE_ROUTE, pattern: '/settings/document-storage' },
  settingsDocumentStorageType: {
    ...DOCUMENT_STORAGE_ROUTE,
    pattern: '/settings/document-storage/types/[id]',
    params: ['id'],
  },
  settingsInternalFeatureFlags: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('__feature-flags'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsForms: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('forms'),
    availability: always(),
    authorization: configGated('workflows_and_forms', superAdminOrP2pAdmin),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsImport: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('import'),
    availability: always(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsInherentRiskAssessment: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('inherent-risk-assessment'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), ldFlag('RELEASE_INHERENT_RISK_ASSESSMENT')]),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsIntegrations: { ...INTEGRATIONS_ROUTE, pattern: SETTINGS_PATTERN, segment: modeSegment('integrations') },
  settingsIntegrationEntities: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/entities',
    params: ['integrationId'],
  },
  settingsIntegrationEntityFields: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/fields/[entityType]',
    params: ['integrationId', 'entityType'],
  },
  settingsIntegrationFilters: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/filters',
    params: ['integrationId'],
  },
  settingsIntegrationPermissions: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/permissions',
    params: ['integrationId'],
  },
  settingsIntegrationProcedures: {
    ...INTEGRATION_BUILDER_ROUTE,
    pattern: '/settings/integrations/[integrationId]/procedures',
    params: ['integrationId'],
  },
  settingsIntegrationProcedureTemplate: {
    ...INTEGRATION_BUILDER_ROUTE,
    pattern: '/settings/integrations/[integrationId]/procedures/[templateId]',
    params: ['integrationId', 'templateId'],
  },
  settingsIntegrationVariables: {
    ...INTEGRATION_BUILDER_ROUTE,
    pattern: '/settings/integrations/[integrationId]/variables',
    params: ['integrationId'],
  },
  settingsLocalisation: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('localisation'),
    availability: orgFlag(FeatureFlagsEnum.CONFIG_TRANSLATIONS),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsPolicies: {
    pattern: '/settings/policies',
    availability: always(),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  // A policy is shared by link, so its own page stays reachable by anyone the documents service will
  // answer for; the store that lists every policy does not.
  settingsPolicy: { pattern: '/settings/policies/[id]', params: ['id'], availability: always(), authorization: { kind: 'open' } },
  settingsPreferences: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('preferences'),
    availability: always(),
    authorization: { kind: 'open' },
  },
  settingsProductCatalogue: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('product-catalogue'),
    availability: orgFlag(FeatureFlagsEnum.PRODUCTS_AND_SERVICES_CATALOGUE),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsPunchoutCatalogues: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('punchout-catalogues'),
    availability: all([omneanOnly(), orgFlag(FeatureFlagsEnum.PUNCHOUT_CATALOGUES), ldFlag('SHOW_PUNCHOUT_CATALOGUES')]),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsHostedCatalogues: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('hosted-catalogues'),
    availability: orgFlag(FeatureFlagsEnum.HOSTED_CATALOGUES),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsCategories: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('categories'),
    availability: any([
      orgFlag(FeatureFlagsEnum.PRODUCTS_AND_SERVICES_CATALOGUE),
      orgFlag(FeatureFlagsEnum.SUPPLIER_CATEGORISATION),
    ]),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsRisks: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('risks'),
    availability: orgFlag(FeatureFlagsEnum.TPRM),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsRiskManagement: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('risk-management'),
    availability: orgFlag(FeatureFlagsEnum.TPRM),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsRiskAlerts: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('risk-alerts'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), orgFlag(FeatureFlagsEnum.TPRM_CONTINUOUS_MONITORING)]),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsSubprocessorSettings: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('subprocessor-settings'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), orgFlag(FeatureFlagsEnum.SUPPLIER_SUBPROCESSORS)]),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsArtefactCatalogue: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('artefact-catalogue'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), ldFlag('RELEASE_ARTEFACTS_AGENT')]),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsArtefactGroups: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('artefact-groups'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), ldFlag('RELEASE_ARTEFACTS_AGENT')]),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsArtefactsLibrary: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('artefacts-library'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), ldFlag('RELEASE_ARTEFACTS_AGENT')]),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsGoodsReceipting: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('goods-receipting'),
    availability: orgFlag(FeatureFlagsEnum.GOODS_RECEIPTING),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsInvoiceProcessing: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('invoice-processing'),
    availability: orgFlag(FeatureFlagsEnum.INVOICE_PROCESSING),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsSecurity: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('security'),
    availability: always(),
    authorization: configGated('it'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsSourcing: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('sourcing'),
    availability: orgFlag(FeatureFlagsEnum.RFX),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsSourcingMemory: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('sourcing-memory'),
    availability: all([omneanOnly(), orgFlag(FeatureFlagsEnum.RFX)]),
    authorization: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsUserProperties: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('user-properties'),
    availability: always(),
    authorization: configGated('it'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsUsersAndGroups: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('users-and-groups'),
    availability: always(),
    authorization: configGated('users_and_groups'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsWorkflows: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('workflows'),
    availability: always(),
    authorization: configGated('workflows_and_forms', superAdminOrP2pAdmin),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsWorkflowDraftEdit: {
    ...WORKFLOW_EDITOR_ROUTE,
    pattern: '/settings/workflows/edit/[id]/drafts/[draftId]',
    params: ['id', 'draftId'],
  },
  settingsWorkflowEdit: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/settings/workflows/edit/[id]', params: ['id'] },
} as const satisfies Readonly<Record<RouteKey, RouteDefinition>>

// `Object.entries` widens the key to `string`; `ROUTE_REGISTRY` is `Record<RouteKey, …>`, so every key is a `RouteKey`.
export const ROUTE_REGISTRY_ENTRIES = Object.entries(ROUTE_REGISTRY) as readonly [RouteKey, RouteDefinition][]
